// tslint:disable: no-empty
import React, { Context, createContext, PropsWithChildren, useCallback, useContext, useState } from 'react'

import { callApi, TApiResponse } from '../api'
import { useAuthContext } from '../auth'
import {
	IExpertProfiles,
	TExpertProfile,
	TExpertProfileActions,
	TExpertProfileRequest,
	TExpertAccountRequest,
} from '../types'
import { profileHeaderToken } from '../utils'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialExpertProfilesState: IExpertProfiles = {
	profiles: [],
	loading: false,
}

const ExpertProfilesContext: Context<{
	expertProfiles: IExpertProfiles
	actions: TExpertProfileActions
}> = createContext({
	expertProfiles: initialExpertProfilesState,
	actions: {
		getExpertProfiles: (token?: string) => {},
		updateExpertAccount: (id: number, profile: TExpertAccountRequest, token?: string) => ({} as Promise<TApiResponse>),
		updateExpertProfile: (id: number, profile: TExpertProfileRequest, token?: string) => ({} as Promise<TApiResponse>),
		setError: () => {},
		setProfiles: (profiles: TExpertProfile[]) => {},
	},
})

export const ExpertProfilesProvider = (props: PropsWithChildren<{}>) => {
	const { actions: authActions } = useAuthContext()
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setProviderError] = useState<string | null>(null)
	const [profiles, setProfiles] = useState<TExpertProfile[]>([])

	const fetchExpertProfiles = async (token?: string) => {
		setLoading(true)
		const expertProfileResponse: TApiResponse = await callApi(
			'account/expert',
			token ? { headers: profileHeaderToken(token) } : {}
		)

		const { data } = expertProfileResponse.payload

		if (expertProfileResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setProfiles(data.profiles)
			authActions.setUserFields(data.profiles[0]?.user || {})
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot get Expert Profile!')
			if (expertProfileResponse.status === 401) {
				authActions.logout()
			}
		}
	}

	const fetchUpdateExpertAccount = async (
		id: number,
		body: TExpertAccountRequest,
		token?: string
	): Promise<TApiResponse> => {
		setLoading(true)
		const updateExpertProfileResponse: TApiResponse = await callApi(`account/expert/${id}`, {
			method: 'put',
			body,
			headers: token ? profileHeaderToken(token) : {},
		})

		const { data } = updateExpertProfileResponse.payload

		if (updateExpertProfileResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setProfiles(profiles.map((p: TExpertProfile) => (p.id === data.id ? data : p)))
			authActions.setUserFields(data?.user || {})
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot update Expert Profile!')
			if (updateExpertProfileResponse.status === 401) {
				authActions.logout()
			}
		}

		return updateExpertProfileResponse
	}

	const updateExpertProfile = async (
		id: number,
		body: TExpertProfileRequest,
		token?: string
	): Promise<TApiResponse> => {
		setLoading(true)
		const updateExpertProfileResponse: TApiResponse = await callApi(`account/expert/profile/${id}`, {
			method: 'put',
			body,
			headers: token ? profileHeaderToken(token) : {},
		})

		const { data } = updateExpertProfileResponse.payload
		if (updateExpertProfileResponse.ok) {
			setLoading(false)
			setProviderError(null)
			setProfiles(profiles.map((p: TExpertProfile) => (p.id === data.id ? { ...p, ...data } : p)))
		} else {
			setLoading(false)
			setProviderError(data.message || 'Cannot update Expert Profile!')
			if (updateExpertProfileResponse.status === 401) {
				authActions.logout()
			}
		}
		return updateExpertProfileResponse
	}

	const getExpertProfiles = useCallback(fetchExpertProfiles, [authActions])
	const updateExpertAccount = useCallback(fetchUpdateExpertAccount, [authActions, profiles])

	const setError = useCallback(
		(errorMessage?: string) => {
			setProviderError(errorMessage || null)
		},
		[setProviderError]
	)

	const actions: TExpertProfileActions = {
		getExpertProfiles,
		updateExpertAccount,
		updateExpertProfile,
		setError,
		setProfiles,
	}

	return (
		<ExpertProfilesContext.Provider value={{ expertProfiles: { profiles, loading, error }, actions }}>
			{props.children}
		</ExpertProfilesContext.Provider>
	)
}

export const useExpertProfilesContext: () => {
	expertProfiles: IExpertProfiles
	actions: TExpertProfileActions
} = () => {
	return useContext(ExpertProfilesContext)
}
